import React from 'react'
import {
  Button,
  CheckboxProps,
  DropdownProps,
  Form,
  Icon,
  InputOnChangeData,
  Loader,
  Message,
  TextAreaProps,
} from 'semantic-ui-react'

import DataSelect from '../../components/form/dataSelect'
import { Company, RecurringCharge, recurringChargeSchema } from '../../types'
import { useApi } from '../../store/mainContext'
import { validateResponse } from '../../utils'

type RecurringChargeFormState = {
  customerId: string
  amount: string
  description: string
  active: boolean
}

interface RecurringChargeSidePanelProps {
  open: boolean
  recurringChargeId?: string
  onClose(): void
  onSaved(): void
  onDeleted(): void
}

const createBlankFormState = (): RecurringChargeFormState => ({
  customerId: '',
  amount: '',
  description: '',
  active: true,
})

const createFormStateFromRecurringCharge = (
  charge: RecurringCharge,
): RecurringChargeFormState => ({
  customerId: charge.customerId,
  amount:
    typeof charge.amount === 'number' && !Number.isNaN(charge.amount)
      ? String(charge.amount)
      : '',
  description: charge.description ?? '',
  active: Boolean(charge.active),
})

const editableFields: Array<keyof RecurringChargeFormState> = [
  'customerId',
  'amount',
  'description',
  'active',
]

const RecurringChargeSidePanel = ({
  open,
  recurringChargeId,
  onClose,
  onSaved,
  onDeleted,
}: RecurringChargeSidePanelProps) => {
  const api = useApi()

  const isNew = !recurringChargeId

  const [formState, setFormState] =
    React.useState<RecurringChargeFormState | null>(null)
  const [baseline, setBaseline] =
    React.useState<RecurringChargeFormState | null>(null)
  const [isLoading, setIsLoading] = React.useState(false)
  const [isSaving, setIsSaving] = React.useState(false)
  const [isDeleting, setIsDeleting] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!open) {
      setFormState(null)
      setBaseline(null)
      setError(null)
      setIsLoading(false)
      setIsSaving(false)
      setIsDeleting(false)
      return
    }

    if (!recurringChargeId) {
      const blank = createBlankFormState()
      setFormState(blank)
      setBaseline(blank)
      setError(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setError(null)

    api
      .get(`billing-recurring-charges/${recurringChargeId}`)
      .then(validateResponse(recurringChargeSchema))
      .then((charge) => {
        const state = createFormStateFromRecurringCharge(charge)
        setFormState(state)
        setBaseline(state)
      })
      .catch((err) => {
        console.error(err)
        setError('Unable to load recurring charge details.')
      })
      .finally(() => setIsLoading(false))
  }, [api, open, recurringChargeId])

  const isDirty = React.useMemo(() => {
    if (!baseline || !formState) return false
    return editableFields.some((field) => baseline[field] !== formState[field])
  }, [baseline, formState])

  const descriptionValue = formState?.description.trim() ?? ''
  const amountValue = formState ? Number.parseFloat(formState.amount) : NaN
  const hasRequiredFields =
    Boolean(formState?.customerId) &&
    descriptionValue.length > 0 &&
    !Number.isNaN(amountValue)

  const canSave =
    Boolean(formState) && hasRequiredFields && (isNew || isDirty) && !isSaving

  const handleFieldChange = <Key extends keyof RecurringChargeFormState>(
    field: Key,
    value: RecurringChargeFormState[Key],
  ) => {
    setFormState((prev) => (prev ? { ...prev, [field]: value } : prev))
  }

  const handleStringInput =
    (field: keyof RecurringChargeFormState) =>
    (
      _event: React.SyntheticEvent<HTMLInputElement>,
      data: InputOnChangeData,
    ) => {
      handleFieldChange(
        field,
        String(data.value ?? '') as RecurringChargeFormState[typeof field],
      )
    }

  const handleTextAreaInput =
    (field: keyof RecurringChargeFormState) =>
    (
      _event: React.SyntheticEvent<HTMLTextAreaElement>,
      data: TextAreaProps,
    ) => {
      handleFieldChange(
        field,
        String(data.value ?? '') as RecurringChargeFormState[typeof field],
      )
    }

  const handleCustomerChange = (
    _event: React.SyntheticEvent<HTMLElement>,
    data: DropdownProps,
  ) => {
    handleFieldChange('customerId', (data.value as string | undefined) || '')
  }

  const handleActiveChange = (
    _event: React.FormEvent<HTMLInputElement>,
    data: CheckboxProps,
  ) => {
    handleFieldChange('active', Boolean(data.checked))
  }

  const handleSave = async () => {
    if (!formState) return

    const parsedAmount = Number.parseFloat(formState.amount)
    if (Number.isNaN(parsedAmount)) return

    setIsSaving(true)
    setError(null)

    const payload: Partial<RecurringCharge> = {
      customerId: formState.customerId,
      description: descriptionValue,
      amount: parsedAmount,
      active: formState.active,
    }

    try {
      if (isNew) {
        await api.post('billing-recurring-charges', payload)
      } else {
        await api.patch(
          `billing-recurring-charges/${recurringChargeId}`,
          payload,
        )
      }
      onSaved()
    } catch (err) {
      console.error(err)
      setError('Failed to save recurring charge.')
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async () => {
    if (!recurringChargeId) return

    const confirmed = window.confirm(
      'Are you sure you want to delete this recurring charge? This action cannot be undone.',
    )

    if (!confirmed) return

    setIsDeleting(true)
    setError(null)
    try {
      await api.delete(`billing-recurring-charges/${recurringChargeId}`)
      onDeleted()
    } catch (err) {
      console.error(err)
      setError('Failed to delete recurring charge.')
    } finally {
      setIsDeleting(false)
    }
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault()
    event.stopPropagation()
    if (isSaving || isDeleting || !canSave) return
    handleSave()
  }

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-slate-900/60"
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-lg flex-col bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {error && (
            <Message negative>
              <Message.Header>Something went wrong</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          <div className="flex justify-between">
            <h1 className="text-xl font-semibold text-slate-900">
              {isNew ? 'Add Recurring Charge' : 'Edit Recurring Charge'}
            </h1>
            <Button
              icon
              basic
              size="small"
              aria-label="Close recurring charge panel"
              onClick={onClose}
              disabled={isSaving || isDeleting}
            >
              <Icon name="close" />
            </Button>
          </div>

          {isLoading || !formState ? (
            <div className="flex h-full items-center justify-center py-10">
              <Loader active inline="centered" />
            </div>
          ) : (
            <Form
              onSubmit={handleSubmit}
              autoComplete="off"
              loading={isSaving}
              className="flex h-full flex-col"
            >
              <div className="flex flex-col gap-6">
                <div className="space-y-4">
                  <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
                    Charge Details
                  </h2>

                  <Form.Field required>
                    <label>Customer</label>
                    <DataSelect
                      selection
                      search
                      clearable
                      placeholder="Search customer"
                      url="companies"
                      render={(company: Company) => company.name}
                      value={formState.customerId || undefined}
                      onChange={handleCustomerChange}
                    />
                  </Form.Field>

                  <Form.Input
                    label="Amount (USD)"
                    type="number"
                    min="0"
                    step="0.01"
                    required
                    value={formState.amount}
                    onChange={handleStringInput('amount')}
                  />

                  <Form.TextArea
                    label="Description"
                    placeholder="Describe this recurring charge"
                    required
                    rows={3}
                    value={formState.description}
                    onChange={handleTextAreaInput('description')}
                  />

                  <Form.Checkbox
                    label="Active"
                    toggle
                    checked={formState.active}
                    onChange={handleActiveChange}
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 border-slate-200 pb-10 pt-4">
                <Button
                  basic
                  onClick={onClose}
                  disabled={isSaving || isDeleting}
                >
                  <Icon name="remove" />
                  Cancel
                </Button>
                {!isNew && (
                  <Button
                    basic
                    color="red"
                    onClick={handleDelete}
                    loading={isDeleting}
                    disabled={isDeleting || isSaving}
                  >
                    <Icon name="trash" />
                    Delete
                  </Button>
                )}
                <Button
                  primary
                  type="submit"
                  loading={isSaving}
                  disabled={!canSave || isDeleting}
                >
                  <Icon name="save" />
                  Save
                </Button>
              </div>
            </Form>
          )}
        </div>
      </div>
    </div>
  )
}

export default RecurringChargeSidePanel
